import { type FC, useEffect, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { OptionCard } from "@/shared/components/ui/OptionCard";
import { RadioGroup } from "@/shared/components/ui/radio-group";
import { Separator } from "@/shared/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { supabase } from "@/shared/lib/supabase";
import { useUser } from "@/shared/hooks/useAppContext";
import { useAuthorityAnswers, deriveAuthorityBucket } from "@/shared/hooks/useAuthorityAnswers";
import { EventType, trackEvent } from "@/shared/utils/core";

import { TriageModalStepLayout } from "./TriageModalStepLayout";

type AppointmentAnswer = "me" | "someone-else" | "not-yet" | "not-sure";
type TrustAnswer = "me-trustee" | "someone-else-trustee" | "no-trust" | "not-sure";

export interface AuthorityInviteData {
  inviteeName: string;
  inviteRole: "executor" | "administrator" | "trustee";
}

interface TriageModalAuthorityStepProps {
  personFirstName: string;
  estateId: number;
  onBack: () => void;
  onContinue: (authorityBucket: string) => void;
  onInvite: (data: AuthorityInviteData[], authorityBucket: string) => void;
}

const APPOINTMENT_OPTIONS: {
  value: AppointmentAnswer;
  label: string;
  hasTextInput?: boolean;
  textInputPlaceholder?: string;
}[] = [
  { value: "me", label: "Yes, the court appointed me" },
  {
    value: "someone-else",
    label: "Yes, someone else was appointed",
    hasTextInput: true,
    textInputPlaceholder: "Enter their name",
  },
  { value: "not-yet", label: "No, not yet" },
  { value: "not-sure", label: "I'm not sure what this is" },
];

const TRUST_OPTIONS: {
  value: TrustAnswer;
  label: string;
  hasTextInput?: boolean;
  textInputPlaceholder?: string;
}[] = [
  { value: "me-trustee", label: "Yes, and I'm the trustee" },
  {
    value: "someone-else-trustee",
    label: "Yes, someone else is the trustee",
    hasTextInput: true,
    textInputPlaceholder: "Enter their name",
  },
  { value: "no-trust", label: "No trust" },
  { value: "not-sure", label: "I'm not sure" },
];

const DefinitionTrigger: FC<{ children: string; definition: string }> = ({
  children,
  definition,
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className="cursor-help underline decoration-dotted underline-offset-[6px]">
        {children}
      </span>
    </TooltipTrigger>
    <TooltipContent>{definition}</TooltipContent>
  </Tooltip>
);

export const TriageModalAuthorityStep: FC<TriageModalAuthorityStepProps> = ({
  personFirstName,
  estateId,
  onBack,
  onContinue,
  onInvite,
}) => {
  const personName = personFirstName || "the person who passed";
  const user = useUser();
  const queryClient = useQueryClient();
  const { savedAnswers, saveAnswers } = useAuthorityAnswers(estateId);

  const [appointment, setAppointment] = useState<AppointmentAnswer | "">("");
  const [trust, setTrust] = useState<TrustAnswer | "">("");
  const [appointmentTextInputs, setAppointmentTextInputs] = useState<Record<string, string>>({});
  const [trustTextInputs, setTrustTextInputs] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const hasRestored = useRef(false);

  useEffect(() => {
    if (!savedAnswers || hasRestored.current) return;
    hasRestored.current = true;

    if (savedAnswers.appointment) {
      setAppointment(savedAnswers.appointment as AppointmentAnswer);
    }
    if (savedAnswers.trust) {
      setTrust(savedAnswers.trust as TrustAnswer);
    }
    if (savedAnswers.appointment_name) {
      setAppointmentTextInputs({ "someone-else": savedAnswers.appointment_name });
    }
    if (savedAnswers.trust_name) {
      setTrustTextInputs({ "someone-else-trustee": savedAnswers.trust_name });
    }
  }, [savedAnswers]);
  const [errors, setErrors] = useState<{ appointment?: string; trust?: string }>({});
  const q1Ref = useRef<HTMLDivElement>(null);
  const q2Ref = useRef<HTMLDivElement>(null);

  const validate = (): boolean => {
    const next: { appointment?: string; trust?: string } = {};
    if (!appointment) next.appointment = "Choose an answer to continue";
    if (!trust) next.trust = "Choose an answer to continue";
    setErrors(next);

    if (Object.keys(next).length > 0) {
      const target = next.appointment ? q1Ref.current : q2Ref.current;
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    return true;
  };

  const handleContinue = async () => {
    if (!validate() || !user?.user_id) return;

    setSaving(true);
    try {
      const updates: Record<string, boolean> = {
        is_executor: appointment === "me",
        is_administrator: false,
        is_trustee: trust === "me-trustee",
        is_nominated: appointment === "me",
      };

      const { error: roleError } = await supabase
        .from("user_estates")
        .update(updates)
        .eq("user_id", user.user_id)
        .eq("estate_id", estateId);
      if (roleError) throw roleError;

      const authorityBucket = String(deriveAuthorityBucket(appointment, trust));
      await saveAnswers({
        appointment,
        trust,
        authority_bucket: deriveAuthorityBucket(appointment, trust),
        ...(appointmentTextInputs["someone-else"]
          ? { appointment_name: appointmentTextInputs["someone-else"] }
          : {}),
        ...(trustTextInputs["someone-else-trustee"]
          ? { trust_name: trustTextInputs["someone-else-trustee"] }
          : {}),
      });

      queryClient.invalidateQueries({ queryKey: ["userEstateRole"] });

      const invites: AuthorityInviteData[] = [];
      if (appointment === "someone-else") {
        invites.push({
          inviteeName: appointmentTextInputs["someone-else"] ?? "",
          inviteRole: "executor",
        });
      }
      if (trust === "someone-else-trustee") {
        invites.push({
          inviteeName: trustTextInputs["someone-else-trustee"] ?? "",
          inviteRole: "trustee",
        });
      }

      trackEvent(EventType.TRIAGE_AUTHORITY_SUBMITTED, {
        estate_id: String(estateId),
        appointment_answer: appointment,
        trust_answer: trust,
        authority_bucket: authorityBucket,
        invite_queue_count: invites.length,
      });

      if (invites.length > 0) {
        onInvite(invites, authorityBucket);
      } else {
        onContinue(authorityBucket);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <TriageModalStepLayout
      title="Legal authority"
      description={`Two quick questions about who can act for ${personName}'s estate. There's no wrong answer, since your path starts from wherever you are.`}
      activeStep={3}
      onBack={onBack}
      actionButton={
        <Button variant="brand" size="md" onClick={handleContinue} disabled={saving}>
          Continue
        </Button>
      }
    >
      <Separator />

      <TooltipProvider>
        <div ref={q1Ref} className="flex flex-col gap-6">
          <p className="text-base font-semibold leading-[1.55] tracking-[-0.32px]">
            Has the court named an{" "}
            <DefinitionTrigger definition="The executor is the person in charge of the estate when there's a will. The will names who it should be, and the court confirms that choice by giving this person legal authority to act for the person who passed.">
              executor
            </DefinitionTrigger>{" "}
            or{" "}
            <DefinitionTrigger definition="The administrator is the person in charge of the estate when there isn't a will. The court picks this person, usually the closest relative, and gives them legal authority to act for the person who passed.">
              administrator
            </DefinitionTrigger>
            ?
          </p>

          <RadioGroup
            value={appointment}
            onValueChange={(v) => {
              setAppointment(v as AppointmentAnswer);
              setErrors((prev) => ({ ...prev, appointment: undefined }));
            }}
            className="flex flex-col gap-4"
          >
            {APPOINTMENT_OPTIONS.map((option) => (
              <OptionCard
                key={option.value}
                label={option.label}
                selected={appointment === option.value}
                onClick={() => {
                  setAppointment(option.value);
                  setErrors((prev) => ({ ...prev, appointment: undefined }));
                }}
                variant="radio"
                value={option.value}
                hasTextInput={option.hasTextInput}
                textInputPlaceholder={option.textInputPlaceholder}
                textInputValue={appointmentTextInputs[option.value]}
                onTextInputChange={
                  option.hasTextInput
                    ? (text) =>
                        setAppointmentTextInputs((prev) => ({
                          ...prev,
                          [option.value]: text,
                        }))
                    : undefined
                }
              />
            ))}
          </RadioGroup>
          <FieldError message={errors.appointment} />
        </div>

        <Separator className="my-0" />

        <div ref={q2Ref} className="flex flex-col gap-6">
          <p className="text-base font-semibold leading-[1.55] tracking-[-0.32px]">
            Did {personName} have a{" "}
            <DefinitionTrigger definition="A trust is a legal setup where a trustee holds property for other people. Property inside a trust usually skips probate, which can save months, but it still has to be found, managed, and passed on.">
              trust
            </DefinitionTrigger>
            ?
          </p>

          <RadioGroup
            value={trust}
            onValueChange={(v) => {
              setTrust(v as TrustAnswer);
              setErrors((prev) => ({ ...prev, trust: undefined }));
            }}
            className="flex flex-col gap-4"
          >
            {TRUST_OPTIONS.map((option) => (
              <OptionCard
                key={option.value}
                label={option.label}
                selected={trust === option.value}
                onClick={() => {
                  setTrust(option.value);
                  setErrors((prev) => ({ ...prev, trust: undefined }));
                }}
                variant="radio"
                value={option.value}
                hasTextInput={option.hasTextInput}
                textInputPlaceholder={option.textInputPlaceholder}
                textInputValue={trustTextInputs[option.value]}
                onTextInputChange={
                  option.hasTextInput
                    ? (text) =>
                        setTrustTextInputs((prev) => ({
                          ...prev,
                          [option.value]: text,
                        }))
                    : undefined
                }
              />
            ))}
          </RadioGroup>
          <FieldError message={errors.trust} />
        </div>
      </TooltipProvider>
    </TriageModalStepLayout>
  );
};
